import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Rating,
  Typography,
  Button
} from '@mui/material'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import Index from '../Index'

import { styled } from '@mui/material/styles'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { ExternalLinks } from '@/@core/services/api'
import { useMutation } from 'react-query'
import {
  analyzeVariant,
  AnalyzeVariantDto,
  getVarDetail,
  GetVariantLiteratureDto
} from '@/@core/services/app.service'
import AnalyzeVariants from './AnalyzeVariant'
import LoadingButton from '@mui/lab/LoadingButton'
import { PsychologyAltOutlined } from '@mui/icons-material'
import { formatScientificToDecimal } from '@/@core/utils/utils'

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={null} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  },
  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
    marginLeft: theme.spacing(1)
  }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}))

const VariationDetail = React.forwardRef(
  (
    props: {
      value: any
      handleOpen: any
      analyzeVariants: any
      analysisInfo: any
      row: any
    },
    ref
  ) => {
    const { t } = useTranslation()

    const [analyzeVariants, setAnalyzeVariant] = React.useState<any>()
    const [isFinish, setIsFinish] = React.useState<boolean>(false)
    const [variantsArticles, setVariantsArticles] = React.useState<any>()
    const AnalyzeVariant = useMutation({
      mutationFn: async (data: AnalyzeVariantDto) => await analyzeVariant(data),
      onSuccess: (datas: any) => {
        //post返回的结果

        setAnalyzeVariant(datas)
      }
    })

    const getVarArticle = useMutation({
      mutationFn: async (params: GetVariantLiteratureDto) =>
        await getVarDetail(params),
      onSuccess: (datas: any) => {
        //post返回的结果
        setVariantsArticles(datas)
      }
    })

    React.useEffect(() => {
      const variantsParams = {
        assembly: props.analysisInfo.genomeBuild,
        variant: `${props.value.variantInfo.chrom}-${props.value.variantInfo.pos}-${props.value.variantInfo.REF}-${props.value.variantInfo.ALT}`,
        gene_name: props.value.variantInfo.gene
      }

      //

      if (variantsParams.assembly) {
        // getVarArticle.mutateAsync(variantsParams)
      }
    }, [props.analysisInfo, props.value])

    const column = React.useMemo(() => {
      return [
        {
          id: 'Population',
          label: 'Population',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        },
        {
          id: 'gnomAD Exomes',
          label: 'gnomAD Exomes',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        },
        {
          id: 'gnomAD Genomes',
          label: 'gnomAD Genomes',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        },
        {
          id: '1000g',
          label: '1000g',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        },
        {
          id: 'ExAC',
          label: 'ExAC',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        },
        {
          id: 'ALFA',
          label: 'ALFA',
          minWidth: 170,
          format: (value: any) => <Typography fontSize={14}>{value}</Typography>
        }
      ]
    }, [props.value])

    const columnCN = React.useMemo(() => {
      return [
        {
          id: 'WBBC_AF',
          label: 'WBBC AF',
          minWidth: 170,
          format: (value: any, row: any) => (
            <Typography fontSize={14}>{value ?? row.wbbc_af}</Typography>
          ),
          renderHeader: () => (
            <Tooltip title={t('Allele frequency in called genotypes in WBBC')}>
              <Typography>WBBC ALL</Typography>
            </Tooltip>
          )
        },
        {
          id: 'WBBC_North_AF',
          label: 'WBBC North AF',
          minWidth: 170,
          format: (value: any, row: any) => (
            <Typography fontSize={14}>{value ?? row.wbbc_north_af}</Typography>
          ),
          renderHeader: () => (
            <Tooltip title={t('Allele frequency in North Han Chinese')}>
              <Typography> North </Typography>
            </Tooltip>
          )
        },
        {
          id: 'WBBC_Central_AF',
          label: 'WBBC Central AF',
          minWidth: 170,
          format: (value: any, row: any) => (
            <Typography fontSize={14}>
              {value ?? row.wbbc_central_af}
            </Typography>
          ),
          renderHeader: () => (
            <Tooltip title={t('Allele frequency in Central Han Chinese')}>
              <Typography> Central </Typography>
            </Tooltip>
          )
        },
        {
          id: 'WBBC_South_AF',
          label: 'WBBC South AF',
          minWidth: 170,
          format: (value: any, row: any) => (
            <Typography fontSize={14}>{value ?? row.wbbc_south_af}</Typography>
          ),
          renderHeader: () => (
            <Tooltip title={t('Total number of alleles in South Han Chinese')}>
              <Typography> South </Typography>
            </Tooltip>
          )
        },
        {
          id: 'WBBC_Lingnan_AF',
          label: 'WBBC Lingnan AF',
          minWidth: 170,
          format: (value: any, row: any) => (
            <Typography fontSize={14}>
              {value ?? row.wbbc_lingnan_af}
            </Typography>
          ),
          renderHeader: () => (
            <Tooltip title={t('Allele frequency in Lingnan Han Chinese')}>
              <Typography> Lingnan </Typography>
            </Tooltip>
          )
        }
      ]
    }, [props.value])

    const column1 = [
      { id: 'name' as const, label: 'Category', width: 100 },
      {
        id: 'code' as 'name',
        label: 'Expected SNVs',
        minWidth: 100
      },
      {
        id: 'population' as 'name',
        label: 'Observed SNVs',
        minWidth: 100
      },
      {
        id: 'size' as 'name',
        label: 'Constraint metric',
        minWidth: 250,
        format: (value: string) => {
          const arr = value.split('_')
          const z = arr[0]
          const oe = arr[1]
          const oe_lower = arr[2]
          const oe_upper = arr[3]

          return (
            <Box>
              <Typography fontSize={12}>{z}</Typography>

              <Typography fontSize={12}>
                {`o/e= ${oe} (${oe_lower}-${oe_upper})`}
              </Typography>
            </Box>
          ) as any
        }
      }
    ]
    interface Data {
      id: number
      name: string
      code: string
      size: number
      population: number
    }

    function createData(
      name: string,
      code: string,
      population: number,
      size: number | string,
      id: number
    ): any {
      return { name, code, population, size, id }
    }

    if (!props.value) return <Box></Box>
    const rows = [
      createData(
        'African',
        `${
          props.value.variantInfo.gnomAD_exome_AFR ??
          props.value.variantInfo.gnomade_afr_af
        }`,
        props.value.variantInfo.gnomAD_genome_AFR ??
          props.value.variantInfo.gnomadg_afr_af,
        props.value.variantInfo.AFR_AF_1000g,
        1
      ),
      createData(
        'American',
        `${
          props.value.variantInfo.gnomAD_exome_AMR ??
          props.value.variantInfo.gnomade_amr_af
        }`,
        props.value.variantInfo.gnomAD_genome_AMR ??
          props.value.variantInfo.gnomadg_amr_af,
        props.value.variantInfo.AMR_AF_1000g,
        2
      ),
      createData(
        'Ashkenazi Jewish',
        `${
          props.value.variantInfo.gnomAD_exome_ASJ ??
          props.value.variantInfo.gnomade_asj_af
        }`,
        props.value.variantInfo.gnomAD_genome_ASJ ??
          props.value.variantInfo.gnomadg_asj_af,
        props.value.variantInfo.ASJ_AF_1000g,
        3
      ),
      createData(
        'East Asian',
        `${
          props.value.variantInfo.gnomAD_exome_EAS ??
          props.value.variantInfo.gnomade_eaf_af
        }`,
        props.value.variantInfo.gnomAD_genome_EAS ??
          props.value.variantInfo.gnomadg_eaf_af,
        props.value.variantInfo.EAS_AF_1000g,
        4
      ),
      createData(
        'European',
        `${
          props.value.variantInfo.gnomAD_exome_FIN ??
          props.value.variantInfo.gnomade_fin_af
        }`,
        props.value.variantInfo.gnomAD_genome_FIN ??
          props.value.variantInfo.gnomadg_fin_af,
        props.value.variantInfo.EUR_AF_1000g,
        5
      ),
      createData(
        'Non-Finnish European',
        `${
          props.value.variantInfo.gnomAD_exome_NFE ??
          props.value.variantInfo.gnomade_nfe_af
        }`,
        props.value.variantInfo.gnomAD_genome_NFE ??
          props.value.variantInfo.gnomadg_nfe_af,
        props.value.variantInfo.NFE_AF_1000g,
        6
      ),

      // createData(
      //   'Other',
      //   `${
      //     props.value.variantInfo.gnomAD_exome_OTH ??
      //     props.value.variantInfo.gnomade_oth_af
      //   }`,
      //   props.value.variantInfo.gnomAD_genome_OTH ??
      //     props.value.variantInfo.gnomade_oth_af,
      //   props.value.variantInfo.gnomAD_genome_AFR ??
      //     props.value.variantInfo.gnomade_afr_af,
      //   7
      // ),
      createData(
        'South Asian',
        `${
          props.value.variantInfo.gnomAD_exome_SAS ??
          props.value.variantInfo.gnomade_sas_af
        }`,
        props.value.variantInfo.gnomAD_exome_SAS ??
          props.value.variantInfo.gnomadg_sas_af,
        props.value.variantInfo.SAS_AF_1000g,
        8
      )
    ]
    const rows2 = [
      createData(
        'Synonymous',
        props.value.genePli?.syn_exp,
        props.value.genePli?.syn_obs,
        `Z=${props.value.genePli?.syn_z}_${props.value.genePli?.syn_oe}_${props.value.genePli?.syn_oe_lower}_${props.value.genePli?.syn_oe_upper}`,
        7
      ),
      createData(
        'Missense',
        `${props.value.genePli?.mis_exp}`,
        props.value.genePli?.mis_obs,
        `Z=${props.value.genePli?.mis_z}_${props.value.genePli?.mis_oe}_${props.value.genePli?.mis_oe_lower}_${props.value.genePli?.mis_oe_upper}`,
        8
      ),
      createData(
        'pLoF',
        `${props.value.genePli?.lof_exp}`,
        props.value.genePli?.lof_obs,
        `pLI=${props.value.genePli?.pLI}_${props.value.genePli?.lof_oe}_${props.value.genePli?.lof_oe_lower}_${props.value.genePli?.lof_oe_upper}`,
        9
      )
    ]

    const data = [
      {
        title: t('Allele frequency')
      }
    ]

    const VariantDescription = [
      {
        key: 'Transcript:',
        value: props.value.variantInfo.transcript
      },
      {
        key: 'Consequence:',
        value: props.value.variantInfo.consequence
      },
      {
        key: 'HGVSc:',
        value: props.value.variantInfo.var_HGVSc ?? props.value.hgvsc
      },
      {
        key: 'HGVSp:',
        value: props.value.variantInfo.var_HGVSp ?? props.value.hgvsp
      },
      {
        key: 'Exon:',
        value: props.value.variantInfo.exon
      },
      {
        key: 'rsID:',
        value: props.value.variantInfo.rs_id ?? props.value.rsid
      },
      {
        key: 'gnomAD:',
        value: 'Link',
        link: `${
          props.analysisInfo.genomeBuild === 'hg38'
            ? ExternalLinks.gnomad_broadinstitute
            : ExternalLinks.gnomad
        }/${props.value.variantInfo.chrom}-${props.value.variantInfo.pos}-${
          props.value.variantInfo.REF ?? props.value.variantInfo.ref
        }-${props.value.variantInfo.ALT ?? props.value.variantInfo.alt}?${
          props.analysisInfo.genomeBuild === 'hg38'
            ? 'dataset=gnomad_r4'
            : 'dataset=gnomad_r2_1'
        }`
      }
    ]

    const ClinicalSignificance = [
      {
        key: 'ClinVar clinical significance:',
        value:
          props.value.variantInfo.CLINVAR_clnsig ??
          props.value.variantInfo.clinvar_clinsig_final
      },
      {
        key: 'Review status:',
        value:
          props.value.variantInfo.CLINVAR_gold_stars ??
          props.value.variantInfo.clinvar_gold_stars
      },
      {
        key: 'Clinvar Variant ID:',
        value: props.value.variantInfo.CLINVAR_variant_id,
        link: `${ExternalLinks.ncbi.variation}/${
          props.value.variantInfo.CLINVAR_variant_id ??
          props.value.variantInfo.clingen_id
        }`
      },
      {
        key: 'OMIM:',
        value: props.value.geneMim,
        link: `${ExternalLinks.omim}/${props.value.geneMim}`
      },
      {
        key: 'REVEL:',
        value:
          props.value.variantInfo.REVEL_score ?? props.value.variantInfo.revel
      },
      {
        key: 'AlphaM:',
        value: `${
          props.value.variantInfo.AM_class ?? props.value.variantInfo.am_class
        }:${
          props.value.variantInfo.AM_pathogenicity ??
          props.value.variantInfo.am_pathogenicity
        }`
      },
      {
        key: 'SpliceAI:',
        value:
          props.value.variantInfo.SpliceAI_max_score ??
          props.value.variantInfo.spliceai_max_score
      },
      {
        key: 'SIFT:',
        value: props.value.SIFT_score ?? props.value.sift
      },
      {
        key: 'Polyphen2:',
        value: props.value.polyphen2_HDIV_score ?? props.value.polyphen
      },

      {
        key: 'CADD_PHRED:',
        value: props.value.variantInfo.CADD_phred ?? props.value.cadd_phred
      },
      {
        key: 'Pubmed ID:',
        value: props.value.variantInfo.pubmed_id ?? props.value.pubmed_ids
      }
    ]

    const ExomesArr = rows.map((item) => {
      if (item.code === '.') {
        return 0
      } else {
        return item.code
      }
    })
    const GenomesArr = rows.map((item) => {
      if ((item.population as any) === '.') {
        return 0
      } else {
        return item.population
      }
    })

    console.log(
      'ExomesArr',
      ExomesArr,
      GenomesArr,
      rows,
      Math.max.apply(null, ExomesArr as unknown as number[])
    )

    const MAX_AF = [
      {
        key: 'MAX_AF in Gnomad Exomes:',
        value: Math.max.apply(null, ExomesArr as unknown as number[])
      },
      {
        key: 'MAX_AF in Gnomad Genomes:',
        value: Math.max.apply(null, GenomesArr)
      },
      {
        key: 'gnomAD_exome_ALL:',
        value: formatScientificToDecimal(
          props.value.variantInfo?.gnomAD_exome_ALL ??
            props.value.variantInfo?.gnomade_af
        )
      },
      {
        key: 'gnomAD_genome_ALL:',
        value: formatScientificToDecimal(
          props.value.variantInfo?.gnomAD_genome_ALL ??
            props.value.variantInfo?.gnomadg_af
        )
      }
    ]

    return (
      <Box>
        {data.map((items) => {
          return (
            <Accordion key={items.title} expanded={true}>
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                sx={{
                  cursor: 'default !important'
                }}
              >
                <Stack width={'100%'} direction={'row'} justifyContent="center">
                  {/* <Typography flex={6}></Typography> */}

                  <Typography
                    sx={{
                      '::before': {
                        content: '""',
                        display: 'inline-block',
                        width: 4,
                        height: '1.25rem',
                        bgcolor: 'primary.main',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: '-10px'
                      },
                      lineHeight: '1.25em',
                      position: 'relative',
                      ml: '10px'
                    }}
                    flex={6}
                  >
                    {items.title}
                  </Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Box>
                  <Grid container spacing={6}>
                    <Grid item xs={12} md={5}>
                      <Stack spacing={6} flex={1}>
                        <Box>
                          <Typography>{t('Variation description')}</Typography>

                          <TableContainer>
                            <Table>
                              <TableBody>
                                {VariantDescription.map((item) => {
                                  if (item.value && item.value !== '.')
                                    return (
                                      <TableRow key={item?.key}>
                                        <TableCell
                                          sx={{
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none'
                                          }}
                                        >
                                          {item?.key}
                                        </TableCell>

                                        <TableCell
                                          sx={{
                                            textAlign: 'right',
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none',
                                            color: item.link
                                              ? 'rgb(51,139,211) !important'
                                              : 'inherit'
                                          }}
                                        >
                                          <Tooltip
                                            title={item?.value}
                                            placement={'top-start'}
                                          >
                                            <Typography
                                              sx={{
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: 'max-content',
                                                float: 'right',
                                                color: item.link
                                                  ? 'rgb(51,139,211) !important'
                                                  : 'inherit',
                                                cursor: item.link
                                                  ? 'pointer'
                                                  : 'inherit',
                                                fontSize: '0.875rem'
                                              }}
                                              onClick={() => {
                                                if (item.link)
                                                  window.open(item.link)
                                              }}
                                            >
                                              {item?.value}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    )
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        <Box>
                          <Typography>{t('Clinical significance')}</Typography>

                          <TableContainer>
                            <Table>
                              <TableBody>
                                {ClinicalSignificance.map((item) => {
                                  if (item.value && item.value !== '.')
                                    return (
                                      <TableRow key={item?.key}>
                                        <TableCell
                                          sx={{
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none'
                                          }}
                                        >
                                          {item?.key}
                                        </TableCell>

                                        <TableCell
                                          sx={{
                                            textAlign: 'right',
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none',
                                            color: item.link
                                              ? 'rgb(51,139,211) !important'
                                              : 'inherit'
                                          }}
                                        >
                                          {item.key === 'Review status:' ? (
                                            <Rating
                                              readOnly
                                              value={Number(item.value)}
                                              max={Number(item.value)}
                                            />
                                          ) : (
                                            <Tooltip
                                              title={item?.value}
                                              placement={'top-start'}
                                            >
                                              <Typography
                                                sx={{
                                                  maxWidth: 200,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  width: 'max-content',
                                                  float: 'right',
                                                  color: item.link
                                                    ? 'rgb(51,139,211) !important'
                                                    : 'inherit',
                                                  cursor: item.link
                                                    ? 'pointer'
                                                    : 'inherit',
                                                  fontSize: '0.875rem'
                                                }}
                                                onClick={() => {
                                                  if (item.link)
                                                    window.open(item.link)
                                                }}
                                              >
                                                {item?.value}
                                              </Typography>
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    )
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        <Box>
                          {props.value.genePli ? (
                            <Index columns={column1} rows={rows2} />
                          ) : (
                            ''
                          )}
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Stack height={'100%'}>
                        <Stack flex={1} paddingTop={2}>
                          <Box overflow={'hidden'}>
                            {variantsArticles?.details &&
                            variantsArticles?.details[0]?.population_data
                              .af_table ? (
                              <Index
                                columns={column as any}
                                rows={
                                  variantsArticles?.details[0]?.population_data
                                    .af_table
                                }
                              />
                            ) : (
                              ''
                            )}
                          </Box>

                          <Box overflow={'hidden'} mt={4}>
                            {props.row ? (
                              <Index
                                columns={columnCN as any}
                                rows={[props.row]}
                              />
                            ) : (
                              ''
                            )}
                          </Box>

                          <Box marginTop={4} width={'100%'}>
                            <TableContainer>
                              <Table>
                                <TableBody>
                                  {MAX_AF.map((item) => {
                                    return (
                                      <TableRow key={item?.key}>
                                        <TableCell
                                          sx={{
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none'
                                          }}
                                        >
                                          {item?.key}
                                        </TableCell>

                                        <TableCell
                                          sx={{
                                            textAlign: 'left',
                                            paddingBottom: '0 !important',
                                            paddingTop: '0 !important',
                                            paddingRight: '0 !important',
                                            border: 'none'
                                          }}
                                        >
                                          {item.value}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Stack>

                        {/* <Box
                          textAlign={'right'}
                          flex={1}
                          display="flex"
                          alignItems={'end'}
                          justifyContent="end"
                        >
                          <Box>
                            <LoadingButton
                              onClick={async () => {
                                const analyzeVariantParams = {
                                  variantInfo: `${props.value.variantInfo.gene},${props.value.variantInfo.c_nomen},${props.value.variantInfo.p_nomen},${props.value.variantInfo.rs_id}`
                                }

                                if (!analyzeVariants) {
                                  // props.handleOpen && props.handleOpen(true)
                                  setIsFinish(true)

                                  await AnalyzeVariant.mutateAsync(
                                    analyzeVariantParams
                                  )

                                  setIsFinish(false)

                                  // props.handleOpen && props.handleOpen(false)
                                } else {
                                  setAnalyzeVariant('')
                                }
                              }}
                              variant="contained"
                              sx={{
                                textTransform: 'capitalize'
                              }}
                              loading={isFinish}
                              endIcon={<PsychologyAltOutlined />}
                            >
                              {t('AI interpretation')}
                            </LoadingButton>
                          </Box>
                        </Box> */}
                      </Stack>
                    </Grid>

                    {/* <Grid item xs={12}>
                      {props.analyzeVariants ? (
                        <AnalyzeVariants value={props.analyzeVariants} />
                      ) : (
                        ''
                      )}
                    </Grid> */}
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    )
  }
)

export default VariationDetail
